/**
 * Auto-split combined court filing bundles by strict court heading markers.
 *
 * A new tab starts only when a paragraph is ALL THREE:
 * 1. Centered
 * 2. ALL CAPS
 * 3. Starts with a recognised court heading prefix (Before/In the Hon'ble, Honorable, Learned, or In the Court of …)
 */

export interface LegalDocumentSegment {
  title: string;
  content: string;
  startBlockIndex: number;
}

export interface DocxSplitAnalysis {
  canSplit: boolean;
  segments: LegalDocumentSegment[];
}

const SPLIT_BLOCK_TAGS = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TABLE",
  "OL",
  "UL",
  "BLOCKQUOTE",
  "HR",
]);

/**
 * Strict tab-boundary prefixes — flexible after the opener (no hardcoded court names).
 * Matches: BEFORE THE HON'BLE …, IN THE HONORABLE …, IN THE COURT OF …, etc.
 */
export const STRICT_COURT_HEADING_PREFIX =
  /^(?:BEFORE\s+THE\s+(?:HONOU?RABLE|HON['']?BLE|LEARNED)|IN\s+THE\s+(?:HONOU?RABLE|HON['']?BLE|LEARNED|COURT\s+OF))\b/i;

const LEGAL_DOC_TITLE =
  /^(?:writ\s+petition|plaint|application\s+for\b|bail\s+application|civil\s+revision|revision\s+petition|constitutional\s+petition|suit\s+for\b|civil\s+appeal|criminal\s+appeal|interlocutory\s+application|review\s+petition|misc\.?\s+application|civil\s+misc\.?\s+application|criminal\s+misc\.?\s+application|written\s+statement|rejoinder|counter[\s-]affidavit|affidavit|memo\s+of\b|urgent\s+application|stay\s+application|injunction\s+application)/i;

interface ParsedBlock {
  el: HTMLElement;
  text: string;
}

function normalizedText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

export function isCenteredBlock(el: HTMLElement): boolean {
  const align = el.style.textAlign?.toLowerCase();
  if (align === "center") return true;
  const attrAlign = el.getAttribute("align")?.toLowerCase();
  if (attrAlign === "center") return true;
  const cls = (el.className || "").toLowerCase();
  return cls.includes("center") || cls.includes("text-center");
}

/** True when every letter in the string is uppercase (ignores digits/punctuation). */
export function isAllCaps(text: string): boolean {
  const letters = text.replace(/[^a-zA-Z]/g, "");
  if (!letters.length) return false;
  return letters === letters.toUpperCase();
}

export function matchesStrictCourtHeadingPrefix(text: string): boolean {
  return STRICT_COURT_HEADING_PREFIX.test(normalizedText(text));
}

/** Strict yardstick for a tab-boundary court heading. */
export function isStrictTabHeadingBlock(block: ParsedBlock): boolean {
  if (!block.text) return false;
  if (!isCenteredBlock(block.el)) return false;
  if (!isAllCaps(block.text)) return false;
  return matchesStrictCourtHeadingPrefix(block.text);
}

function isValidSplitPoint(blocks: ParsedBlock[], index: number): boolean {
  if (index <= 0) return false;
  return isStrictTabHeadingBlock(blocks[index]);
}

function toTabTitle(text: string): string {
  const cleaned = normalizedText(text);
  if (!cleaned) return "";
  if (/^[A-Z0-9\s.\-&,'()/]+$/.test(cleaned)) {
    return cleaned
      .toLowerCase()
      .replace(/\b([a-z])/g, (m) => m.toUpperCase());
  }
  return cleaned.length > 56 ? `${cleaned.slice(0, 53)}…` : cleaned;
}

function inferSegmentTitle(
  blocks: ParsedBlock[],
  startIndex: number,
  endIndex: number,
  fallbackIndex: number,
): string {
  const scanEnd = Math.min(startIndex + 30, endIndex);
  for (let i = startIndex; i < scanEnd; i++) {
    const text = blocks[i].text;
    if (!text) continue;
    if (LEGAL_DOC_TITLE.test(text)) {
      const title = toTabTitle(text);
      if (title) return title;
    }
  }
  return `Document ${fallbackIndex}`;
}

function isSplitBlockElement(el: HTMLElement): boolean {
  if (el.tagName === "DIV") {
    return el.classList.contains("page-break");
  }
  return SPLIT_BLOCK_TAGS.has(el.tagName);
}

function isMeaningfulBlock(el: HTMLElement, text: string): boolean {
  if (el.tagName === "TABLE") return true;
  if (text) return true;
  if (el.getAttribute("style")?.trim()) return true;
  if (el.className?.trim()) return true;
  if (el.querySelector("img, br, table, colgroup")) return true;
  return false;
}

/**
 * Collect top-level document blocks in order (no nested duplication).
 * Descends through wrapper divs from mammoth/Word export.
 */
export function collectTopLevelBlocks(html: string): ParsedBlock[] {
  if (!html.trim()) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="split-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("split-root");
  if (!root) return [];

  const blocks: ParsedBlock[] = [];

  function walk(parent: Node): void {
    for (const node of Array.from(parent.childNodes)) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.tagName === "DIV" && !node.classList.contains("page-break")) {
        walk(node);
        continue;
      }

      if (isSplitBlockElement(node)) {
        const text = normalizedText(node.textContent ?? "");
        if (isMeaningfulBlock(node, text)) {
          blocks.push({ el: node, text });
        }
        continue;
      }

      walk(node);
    }
  }

  walk(root);
  return blocks;
}

/** Serialize blocks without mutating the source tree (clone nodes). */
function blocksToHtml(blocks: ParsedBlock[]): string {
  if (!blocks.length) return "";
  const doc = blocks[0].el.ownerDocument;
  const container = doc.createElement("div");
  for (const block of blocks) {
    container.appendChild(block.el.cloneNode(true));
  }
  return container.innerHTML;
}

function buildSegments(
  blocks: ParsedBlock[],
  splitIndices: number[],
): LegalDocumentSegment[] {
  const points = [...splitIndices, blocks.length];
  const segments: LegalDocumentSegment[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    const start = splitIndices[i];
    const end = points[i + 1];
    const slice = blocks.slice(start, end);
    if (!slice.length) continue;

    segments.push({
      title: inferSegmentTitle(blocks, start, end, i + 1),
      content: blocksToHtml(slice),
      startBlockIndex: start,
    });
  }

  return segments;
}

/** Compare normalized text for split fidelity checks. */
export function normalizedDocumentText(html: string): string {
  if (!html.trim()) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return normalizedText(doc.body.textContent ?? "");
}

/**
 * Analyze imported HTML for repeatable strict court-heading split points.
 */
export function analyzeLegalDocumentSplits(html: string): DocxSplitAnalysis {
  const blocks = collectTopLevelBlocks(html);
  if (blocks.length < 2) {
    return { canSplit: false, segments: [] };
  }

  const splitIndices: number[] = [0];
  for (let i = 1; i < blocks.length; i++) {
    if (isValidSplitPoint(blocks, i)) {
      splitIndices.push(i);
    }
  }

  if (splitIndices.length < 2) {
    return { canSplit: false, segments: [] };
  }

  const segments = buildSegments(blocks, splitIndices);
  if (segments.length < 2) {
    return { canSplit: false, segments: [] };
  }

  return { canSplit: true, segments };
}
